import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  formatDisplayToIso,
  formatIsoToDisplay,
} from 'components/partials/date-picker/helpers/date-format';

import {
  buildRangeKey,
  getUniqueRangesFromCalculation,
  sortRangesForDisplay,
} from '../../matrix-step/definition-tab.constants';
import type {
  CalculationRow,
  Range,
} from '../../matrix-step/definition-tab.constants';

import { splitStoredCeebCodes } from '../helpers/ceeb-codes';
import type { CeebCodesUpdater } from './use-ceeb-codes-input';
import type {
  CeebCodeChip,
  PristineRegimeSelection,
  SpecialAwardAppliesTo,
  SpecialAwardCriteria,
  SpecialAwardCriteriaBlock,
} from '../calculations-step.types';
import {
  EligibilityCriteriaDtoCriteria,
  EligibilityCriteriaDtoOperand,
} from 'api/generated/schemas';
import type {
  CalculationRuleDto,
  CalculationRuleRequest,
  EligibilityCriteriaDto,
} from 'api/generated/schemas';

/** A single awarding-regime calculation cell (calc-matrix entry). */
export interface SpecialAwardRegimeCell {
  id?: number;
  academicRankId?: number | null;
  range?: { from?: number | null; to?: number | null } | null;
}

/** Regime shape needed to build the awarding-regimes source + resolve cell ids. */
export interface SpecialAwardRegime {
  id: string | number;
  name?: string;
  awardType?: string | null;
  calculation?: SpecialAwardRegimeCell[];
}

/** Custom-logic category as returned by `useCustomLogic` (student-data dimensions). */
export interface SpecialAwardLogicCategory {
  category: string;
  options?: Array<{ id: string | number; name: string }>;
}

interface SpecialAwardFormState {
  appliesTo: SpecialAwardAppliesTo;
  criteriaBlocks: SpecialAwardCriteriaBlock[];
  description: string;
  nextBlockId: number;
}

interface UseSpecialAwardFormParams {
  regimes: SpecialAwardRegime[];
  customLogicCategories: SpecialAwardLogicCategory[];
  initialRule?: CalculationRuleDto;
  isPrefillReady?: boolean;
}

/**
 * A rule request built from the modal's population fields only — the caller
 * fills in `awardAmount` / `awardLogic`, which are required by the backend but
 * not known to this hook (they come from the awarding-logic dictionary).
 */
export type SpecialAwardRuleRequest = Omit<
  CalculationRuleRequest,
  'awardAmount' | 'awardLogic'
>;

export interface UseSpecialAwardFormResult {
  appliesTo: SpecialAwardAppliesTo;
  setApplies: (key: keyof SpecialAwardAppliesTo, value: boolean) => void;
  criteriaBlocks: SpecialAwardCriteriaBlock[];
  addCriteriaBlock: () => void;
  removeCriteriaBlock: (blockId: string) => void;
  setBlockType: (blockId: string, type: SpecialAwardCriteria) => void;
  setBlockCategory: (blockId: string, category: string) => void;
  setBlockOptions: (blockId: string, optionIds: Array<string | number>) => void;
  setBlockEligibilityCriteria: (
    blockId: string,
    criteria: EligibilityCriteriaDtoCriteria
  ) => void;
  setBlockOperand: (
    blockId: string,
    operand: EligibilityCriteriaDtoOperand
  ) => void;
  setBlockValue: (
    blockId: string,
    field: 'amount' | 'amountFrom' | 'amountTo' | 'dateFrom' | 'dateTo',
    value: string
  ) => void;
  setBlockCeebCodes: (blockId: string, updater: CeebCodesUpdater) => void;
  setBlockRegimeIds: (blockId: string, ids: Array<string | number>) => void;
  setBlockRangeKeys: (blockId: string, keys: string[]) => void;
  setBlockRankIds: (blockId: string, ids: number[]) => void;
  description: string;
  setDescription: (description: string) => void;
  isValid: boolean;
  toRequests: () => SpecialAwardRuleRequest[];
}

const EMPTY_APPLIES_TO: SpecialAwardAppliesTo = {
  campusVisit: false,
  pellGrant: false,
  fafsaGrant: false,
};

const makeBlockId = (index: number): string => `special-award-block-${index}`;

const createCriteriaBlock = (index: number): SpecialAwardCriteriaBlock => ({
  blockId: makeBlockId(index),
  type: '',
  category: '',
  optionIds: [],
  criteria: '',
  operand: '',
  amount: '',
  amountFrom: '',
  amountTo: '',
  dateFrom: '',
  dateTo: '',
  ceebCodes: [],
  selectedRegimeIds: [],
  selectedRangeKeys: [],
  selectedRankIds: [],
});

const emptyState = (): SpecialAwardFormState => ({
  appliesTo: { ...EMPTY_APPLIES_TO },
  criteriaBlocks: [],
  description: '',
  nextBlockId: 0,
});

const isNumericValue = (raw: string): boolean =>
  raw.trim() !== '' && Number.isFinite(Number(raw));

const isStudentDataBlockComplete = (
  block: SpecialAwardCriteriaBlock
): boolean => !!block.category && !!block.optionIds.length;

/**
 * Maps one Eligibility block to its request DTO, or `null` when the block is
 * still incomplete — which doubles as the block-level validity check.
 *
 * The shape is narrowed to exactly what `AidModelCalculationService
 * .validateEligibilityCriteria` accepts: a CEEB block carries only `values`
 * (the backend normalizes its operand to `IN`), a DISTANCE block carries only
 * `amountFrom`/`amountTo`, an APP_DATE block carries only `dateFrom`/`dateTo`
 * (both backend-normalized to `BETWEEN`, with no operand sent), a `BETWEEN`
 * block carries only the two amount bounds, and every other block carries
 * only `amount`. Sending a field outside that set is a 400.
 */
const toEligibilityCriteriaDto = (
  block: SpecialAwardCriteriaBlock
): EligibilityCriteriaDto | null => {
  if (!block.criteria) {
    return null;
  }

  if (block.criteria === EligibilityCriteriaDtoCriteria.CEEB) {
    if (
      !block.ceebCodes.length ||
      block.ceebCodes.some((chip) => chip.status === 'pending')
    ) {
      return null;
    }

    if (!block.ceebCodes.some((chip) => chip.status === 'matched')) {
      return null;
    }

    return {
      criteria: block.criteria,
      values: block.ceebCodes.map((chip) => chip.code).join(','),
    };
  }

  if (block.criteria === EligibilityCriteriaDtoCriteria.DISTANCE) {
    if (!isNumericValue(block.amountFrom) || !isNumericValue(block.amountTo)) {
      return null;
    }

    const amountFrom = Number(block.amountFrom);
    const amountTo = Number(block.amountTo);

    // FR-5a: an inverted range is incomplete rather than saved — same rationale as the
    // generic BETWEEN case above.
    if (amountFrom > amountTo) {
      return null;
    }

    return { criteria: block.criteria, amountFrom, amountTo };
  }

  if (block.criteria === EligibilityCriteriaDtoCriteria.APP_DATE) {
    const dateFrom = formatDisplayToIso(block.dateFrom);
    const dateTo = formatDisplayToIso(block.dateTo);

    if (!dateFrom || !dateTo) {
      return null;
    }

    // FR-11a: To must be on or after From.
    if (dateFrom > dateTo) {
      return null;
    }

    return { criteria: block.criteria, dateFrom, dateTo };
  }

  if (!block.operand) {
    return null;
  }

  if (block.operand === EligibilityCriteriaDtoOperand.BETWEEN) {
    if (!isNumericValue(block.amountFrom) || !isNumericValue(block.amountTo)) {
      return null;
    }

    const amountFrom = Number(block.amountFrom);
    const amountTo = Number(block.amountTo);

    // An inverted range is treated as incomplete rather than saved: neither the
    // request validation nor the matching engine rejects it, so it would
    // persist as a rule that silently matches nobody.
    if (amountFrom >= amountTo) {
      return null;
    }

    return {
      criteria: block.criteria,
      operand: block.operand,
      amountFrom,
      amountTo,
    };
  }

  return isNumericValue(block.amount)
    ? {
        criteria: block.criteria,
        operand: block.operand,
        amount: Number(block.amount),
      }
    : null;
};

const getSelectedRegimesForBlock = (
  block: SpecialAwardCriteriaBlock,
  regimes: SpecialAwardRegime[]
): SpecialAwardRegime[] => {
  const regimeSet = new Set(block.selectedRegimeIds.map(String));

  return regimes.filter((regime) => regimeSet.has(String(regime.id)));
};

/** Ranges available in the "Need range(s)" select for one Awarding-regimes block. */
export const getAvailableRangesForBlock = (
  block: SpecialAwardCriteriaBlock,
  regimes: SpecialAwardRegime[]
): Range[] => {
  const rows: CalculationRow[] = getSelectedRegimesForBlock(
    block,
    regimes
  ).flatMap((regime) =>
    (regime.calculation ?? []).map((cell) => ({ range: cell.range }))
  );

  return sortRangesForDisplay(getUniqueRangesFromCalculation(rows));
};

/** Resolved calc-matrix cell ids for one Awarding-regimes block's selections. */
export const getRegimeCalculationIdsForBlock = (
  block: SpecialAwardCriteriaBlock,
  regimes: SpecialAwardRegime[]
): number[] => {
  const rangeSet = new Set(block.selectedRangeKeys);
  const rankSet = new Set(block.selectedRankIds);
  const ids: number[] = [];

  getSelectedRegimesForBlock(block, regimes).forEach((regime) => {
    (regime.calculation ?? []).forEach((cell) => {
      if (cell.id == null) {
        return;
      }

      const rangeOk =
        rangeSet.size === 0 ||
        (cell.range != null && rangeSet.has(buildRangeKey(cell.range)));
      const rankOk =
        rankSet.size === 0 ||
        (cell.academicRankId != null && rankSet.has(cell.academicRankId));

      if (rangeOk && rankOk) {
        ids.push(cell.id);
      }
    });
  });

  return ids;
};

const sameSet = <T>(a: T[], b: T[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }

  const bSet = new Set(b);

  return a.every((item) => bSet.has(item));
};

const isRegimeSelectionPristine = (
  block: SpecialAwardCriteriaBlock,
  pristine: PristineRegimeSelection
): boolean =>
  sameSet(block.selectedRegimeIds.map(String), pristine.regimeIds) &&
  sameSet(block.selectedRangeKeys, pristine.rangeKeys) &&
  sameSet(block.selectedRankIds, pristine.rankIds);

/**
 * Submits a regime block's originally-persisted calculation ids unchanged
 * while its regime/range/rank selection stays exactly what it was
 * reconstructed to on Edit prefill (see `buildRegimeBlock`), instead of
 * re-intersecting them through `getRegimeCalculationIdsForBlock`. Once the
 * original rule came from more than one Awarding-regimes block, that
 * re-intersection is unsound — see `buildRegimeBlock`'s doc comment.
 */
const resolveRegimeCalculationIds = (
  block: SpecialAwardCriteriaBlock,
  regimes: SpecialAwardRegime[]
): number[] => {
  const { pristineRegimeSelection } = block;

  if (
    pristineRegimeSelection &&
    isRegimeSelectionPristine(block, pristineRegimeSelection)
  ) {
    return pristineRegimeSelection.calculationIds;
  }

  return getRegimeCalculationIdsForBlock(block, regimes);
};

const isRegimeBlockComplete = (
  block: SpecialAwardCriteriaBlock,
  regimes: SpecialAwardRegime[]
): boolean =>
  !!block.selectedRegimeIds.length &&
  !!resolveRegimeCalculationIds(block, regimes).length;

const isBlockComplete = (
  block: SpecialAwardCriteriaBlock,
  regimes: SpecialAwardRegime[]
): boolean => {
  switch (block.type) {
    case 'STUDENT_DATA':
      return isStudentDataBlockComplete(block);
    case 'AWARDING_REGIMES':
      return isRegimeBlockComplete(block, regimes);
    case 'ELIGIBILITY':
      return !!toEligibilityCriteriaDto(block);
    default:
      return false;
  }
};

/** True when the user has started filling a typed block (not merely picked its type). */
const isBlockPartiallyFilled = (block: SpecialAwardCriteriaBlock): boolean => {
  switch (block.type) {
    case 'STUDENT_DATA':
      return !!block.category || !!block.optionIds.length;
    case 'AWARDING_REGIMES':
      return (
        !!block.selectedRegimeIds.length ||
        !!block.selectedRangeKeys.length ||
        !!block.selectedRankIds.length
      );
    case 'ELIGIBILITY':
      return (
        !!block.criteria ||
        !!block.operand ||
        !!block.amount ||
        !!block.amountFrom ||
        !!block.amountTo ||
        !!block.dateFrom ||
        !!block.dateTo ||
        !!block.ceebCodes.length
      );
    default:
      return false;
  }
};

/** Groups the persisted `customLogicIds` back into one block per dimension. */
const buildStudentDataBlocks = (
  customLogicIds: number[],
  categories: SpecialAwardLogicCategory[],
  nextIndex: number
): SpecialAwardCriteriaBlock[] => {
  const idSet = new Set(customLogicIds.map(String));
  const optionsByCategory = new Map<string, Array<string | number>>();

  categories.forEach((category) => {
    category.options?.forEach((option) => {
      if (!idSet.has(String(option.id))) {
        return;
      }

      const current = optionsByCategory.get(category.category) ?? [];

      current.push(option.id);
      optionsByCategory.set(category.category, current);
    });
  });

  return [...optionsByCategory.entries()].map(
    ([category, optionIds], offset) => ({
      ...createCriteriaBlock(nextIndex + offset),
      type: 'STUDENT_DATA',
      category,
      optionIds,
    })
  );
};

/**
 * Resolves the persisted `regimeCalculationIds` (award-grid cell ids) back
 * into the regime / range / rank selections that produced them, as a single
 * Awarding-regimes block (matching the existing student-data reconstruction,
 * which groups by category rather than by original block).
 *
 * If the original rule's regime calculation ids came from more than one
 * block, this collapses them into one, and re-intersecting the derived
 * selections (regimes × ranges × ranks) no longer yields the same cell ids —
 * it can also pick up cells from combinations that were never actually
 * selected (e.g. block A's regime with block B's range). So the block also
 * carries a `pristineRegimeSelection` snapshot of the exact ids that were
 * matched here; `resolveRegimeCalculationIds` submits those as-is unless the
 * user actually edits this block's regime/range/rank selection.
 */
const buildRegimeBlock = (
  regimeCalculationIds: number[],
  regimes: SpecialAwardRegime[],
  index: number
): SpecialAwardCriteriaBlock => {
  const idSet = new Set(regimeCalculationIds.map(String));
  const selectedRegimeIds: Array<string | number> = [];
  const rangeKeys = new Set<string>();
  const rankIds = new Set<number>();
  const matchedIds: number[] = [];

  regimes.forEach((regime) => {
    let matched = false;

    (regime.calculation ?? []).forEach((cell) => {
      if (cell.id == null || !idSet.has(String(cell.id))) {
        return;
      }

      matched = true;
      matchedIds.push(cell.id);

      if (cell.range != null) {
        rangeKeys.add(buildRangeKey(cell.range));
      }

      if (cell.academicRankId != null) {
        rankIds.add(cell.academicRankId);
      }
    });

    if (matched) {
      selectedRegimeIds.push(regime.id);
    }
  });

  return {
    ...createCriteriaBlock(index),
    type: 'AWARDING_REGIMES',
    selectedRegimeIds,
    selectedRangeKeys: [...rangeKeys],
    selectedRankIds: [...rankIds],
    pristineRegimeSelection: {
      regimeIds: selectedRegimeIds.map(String),
      rangeKeys: [...rangeKeys],
      rankIds: [...rankIds],
      calculationIds: matchedIds,
    },
  };
};

/**
 * FR-32: opening Edit re-validates every previously-saved code live rather than
 * replaying its last-saved match state, so every persisted CEEB code starts as
 * a 'pending' chip — `useCeebCodesInput` resolves them on mount.
 */
const buildCeebCodeChips = (values: string | undefined): CeebCodeChip[] =>
  splitStoredCeebCodes(values).map((code, index) => ({
    id: `ceeb-code-prefill-${index}-${code}`,
    code,
    status: 'pending',
  }));

const buildEligibilityBlocks = (
  criterias: EligibilityCriteriaDto[],
  nextIndex: number
): SpecialAwardCriteriaBlock[] =>
  criterias.map((criteria, offset) => ({
    ...createCriteriaBlock(nextIndex + offset),
    type: 'ELIGIBILITY',
    criteria: criteria.criteria,
    // `IN` is CEEB-only and applied server-side, so it never maps back to a
    // user-selectable condition.
    operand:
      criteria.operand && criteria.operand !== EligibilityCriteriaDtoOperand.IN
        ? criteria.operand
        : '',
    amount: criteria.amount != null ? String(criteria.amount) : '',
    amountFrom: criteria.amountFrom != null ? String(criteria.amountFrom) : '',
    amountTo: criteria.amountTo != null ? String(criteria.amountTo) : '',
    dateFrom: formatIsoToDisplay(criteria.dateFrom),
    dateTo: formatIsoToDisplay(criteria.dateTo),
    ceebCodes: buildCeebCodeChips(criteria.values),
  }));

/** Rebuilds the modal state from a persisted rule so the edit flow opens pre-filled. */
const buildStateFromRule = (
  rule: CalculationRuleDto,
  regimes: SpecialAwardRegime[],
  categories: SpecialAwardLogicCategory[]
): SpecialAwardFormState => {
  const description = rule.description ?? '';
  const appliesTo: SpecialAwardAppliesTo = {
    campusVisit: !!rule.campusVisit,
    pellGrant: !!rule.pellGrant,
    fafsaGrant: !!rule.fafsaGrant,
  };

  const criteriaBlocks: SpecialAwardCriteriaBlock[] = [];

  if (rule.customLogicIds?.length) {
    criteriaBlocks.push(
      ...buildStudentDataBlocks(
        rule.customLogicIds,
        categories,
        criteriaBlocks.length
      )
    );
  }

  if (rule.regimeCalculationIds?.length) {
    criteriaBlocks.push(
      buildRegimeBlock(
        rule.regimeCalculationIds,
        regimes,
        criteriaBlocks.length
      )
    );
  }

  if (rule.eligibilityCriterias?.length) {
    criteriaBlocks.push(
      ...buildEligibilityBlocks(
        rule.eligibilityCriterias,
        criteriaBlocks.length
      )
    );
  }

  return {
    appliesTo,
    criteriaBlocks,
    description,
    nextBlockId: criteriaBlocks.length,
  };
};

export const useSpecialAwardForm = ({
  regimes,
  customLogicCategories,
  initialRule,
  isPrefillReady = true,
}: UseSpecialAwardFormParams): UseSpecialAwardFormResult => {
  const [state, setState] = useState<SpecialAwardFormState>(emptyState);

  // The prefill runs once per edited rule, and only after the queries it reads
  // from have resolved — re-running it would discard the user's edits.
  const prefilledRuleKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!initialRule || !isPrefillReady) {
      return;
    }

    const ruleKey = String(initialRule.id ?? '');

    if (prefilledRuleKeyRef.current === ruleKey) {
      return;
    }

    prefilledRuleKeyRef.current = ruleKey;
    setState(buildStateFromRule(initialRule, regimes, customLogicCategories));
  }, [initialRule, isPrefillReady, regimes, customLogicCategories]);

  const setApplies = useCallback(
    (key: keyof SpecialAwardAppliesTo, value: boolean) =>
      setState((prev) => ({
        ...prev,
        appliesTo: { ...prev.appliesTo, [key]: value },
      })),
    []
  );

  const setDescription = useCallback(
    (description: string) => setState((prev) => ({ ...prev, description })),
    []
  );

  const addCriteriaBlock = useCallback(
    () =>
      setState((prev) => ({
        ...prev,
        criteriaBlocks: [
          ...prev.criteriaBlocks,
          createCriteriaBlock(prev.nextBlockId),
        ],
        nextBlockId: prev.nextBlockId + 1,
      })),
    []
  );

  // Every block always carries its own delete icon, so removal is never
  // gated on how many blocks remain.
  const removeCriteriaBlock = useCallback(
    (blockId: string) =>
      setState((prev) => ({
        ...prev,
        criteriaBlocks: prev.criteriaBlocks.filter(
          (block) => block.blockId !== blockId
        ),
      })),
    []
  );

  // Switching a block's own type discards that block's own fields, scoped to
  // just this one block.
  const setBlockType = useCallback(
    (blockId: string, type: SpecialAwardCriteria) =>
      setState((prev) => ({
        ...prev,
        criteriaBlocks: prev.criteriaBlocks.map((block) =>
          block.blockId === blockId
            ? { ...createCriteriaBlock(0), blockId, type }
            : block
        ),
      })),
    []
  );

  const updateBlock = useCallback(
    (
      blockId: string,
      updater: (block: SpecialAwardCriteriaBlock) => SpecialAwardCriteriaBlock
    ) =>
      setState((prev) => ({
        ...prev,
        criteriaBlocks: prev.criteriaBlocks.map((block) =>
          block.blockId === blockId ? updater(block) : block
        ),
      })),
    []
  );

  const setBlockCategory = useCallback(
    (blockId: string, category: string) =>
      updateBlock(blockId, (block) => ({ ...block, category, optionIds: [] })),
    [updateBlock]
  );

  const setBlockOptions = useCallback(
    (blockId: string, optionIds: Array<string | number>) =>
      updateBlock(blockId, (block) => ({ ...block, optionIds })),
    [updateBlock]
  );

  const setBlockEligibilityCriteria = useCallback(
    (blockId: string, criteria: EligibilityCriteriaDtoCriteria) =>
      updateBlock(blockId, (block) => ({
        ...createCriteriaBlock(0),
        blockId,
        type: block.type,
        criteria,
      })),
    [updateBlock]
  );

  const setBlockOperand = useCallback(
    (blockId: string, operand: EligibilityCriteriaDtoOperand) =>
      updateBlock(blockId, (block) => ({
        ...block,
        operand,
        amount: '',
        amountFrom: '',
        amountTo: '',
      })),
    [updateBlock]
  );

  const setBlockValue = useCallback(
    (
      blockId: string,
      field: 'amount' | 'amountFrom' | 'amountTo' | 'dateFrom' | 'dateTo',
      value: string
    ) => updateBlock(blockId, (block) => ({ ...block, [field]: value })),
    [updateBlock]
  );

  // Async validation responses can resolve out of order, so the update is
  // expressed as a functional updater over the block's latest `ceebCodes`
  // rather than a value snapshotted at commit time (see `useCeebCodesInput`).
  const setBlockCeebCodes = useCallback(
    (blockId: string, updater: CeebCodesUpdater) =>
      updateBlock(blockId, (block) => ({
        ...block,
        ceebCodes: updater(block.ceebCodes),
      })),
    [updateBlock]
  );

  const setBlockRegimeIds = useCallback(
    (blockId: string, ids: Array<string | number>) =>
      updateBlock(blockId, (block) => ({ ...block, selectedRegimeIds: ids })),
    [updateBlock]
  );

  const setBlockRangeKeys = useCallback(
    (blockId: string, keys: string[]) =>
      updateBlock(blockId, (block) => ({ ...block, selectedRangeKeys: keys })),
    [updateBlock]
  );

  const setBlockRankIds = useCallback(
    (blockId: string, ids: number[]) =>
      updateBlock(blockId, (block) => ({ ...block, selectedRankIds: ids })),
    [updateBlock]
  );

  // Add enables as soon as any one source is fully valid, regardless of
  // whether other things on screen are still incomplete: a checked preset by
  // itself is enough, and so is any single complete block — an empty typed
  // block (type chosen, no fields touched) never blocks it.
  //
  // Exception: a pending CEEB chip blocks Add/Save outright, even when some
  // other source is already complete. Prefilled CEEB codes re-validate
  // asynchronously and start `pending` (see `buildCeebCodeChips`), so without
  // this, submitting while validation is still in flight would send the
  // block through `toEligibilityCriteriaDto` as incomplete (it returns `null`
  // while any chip is pending) and silently drop a previously-saved CEEB
  // criterion from the request instead of waiting for it to resolve.
  //
  // Exception: a partially filled but incomplete block blocks Add/Save even
  // when another source is already complete — `toRequests` omits incomplete
  // blocks, which would silently drop a persisted criterion on Edit.
  const isValid = useMemo(() => {
    const hasPendingCeebChip = state.criteriaBlocks.some((block) =>
      block.ceebCodes.some((chip) => chip.status === 'pending')
    );

    if (hasPendingCeebChip) {
      return false;
    }

    const hasPartialIncompleteBlock = state.criteriaBlocks.some(
      (block) =>
        !!block.type &&
        isBlockPartiallyFilled(block) &&
        !isBlockComplete(block, regimes)
    );

    if (hasPartialIncompleteBlock) {
      return false;
    }

    const hasPreset =
      state.appliesTo.campusVisit ||
      state.appliesTo.pellGrant ||
      state.appliesTo.fafsaGrant;

    return (
      hasPreset ||
      state.criteriaBlocks.some((block) => isBlockComplete(block, regimes))
    );
  }, [state.appliesTo, state.criteriaBlocks, regimes]);

  // Everything valid in one Add — every checked preset plus every complete
  // block's result — collapses into a single request: at most one request,
  // never one per source.
  const toRequests = useCallback((): SpecialAwardRuleRequest[] => {
    const description = state.description.trim() || undefined;

    const customLogicIds = [
      ...new Set(
        state.criteriaBlocks
          .filter(
            (block) =>
              block.type === 'STUDENT_DATA' && isStudentDataBlockComplete(block)
          )
          .flatMap((block) => block.optionIds)
          .map(Number)
          .filter((value) => Number.isFinite(value))
      ),
    ];

    const regimeCalculationIds = [
      ...new Set(
        state.criteriaBlocks
          .filter((block) => block.type === 'AWARDING_REGIMES')
          .flatMap((block) => resolveRegimeCalculationIds(block, regimes))
      ),
    ];

    const eligibilityCriterias = state.criteriaBlocks
      .filter((block) => block.type === 'ELIGIBILITY')
      .map(toEligibilityCriteriaDto)
      .filter((criteria): criteria is EligibilityCriteriaDto => !!criteria);

    const hasAnySource =
      state.appliesTo.campusVisit ||
      state.appliesTo.pellGrant ||
      state.appliesTo.fafsaGrant ||
      !!customLogicIds.length ||
      !!regimeCalculationIds.length ||
      !!eligibilityCriterias.length;

    if (!hasAnySource) {
      return [];
    }

    return [
      {
        description,
        campusVisit: state.appliesTo.campusVisit,
        pellGrant: state.appliesTo.pellGrant,
        fafsaGrant: state.appliesTo.fafsaGrant,
        customLogicIds,
        regimeCalculationIds,
        eligibilityCriterias,
      },
    ];
  }, [state.description, state.appliesTo, state.criteriaBlocks, regimes]);

  return {
    appliesTo: state.appliesTo,
    setApplies,
    criteriaBlocks: state.criteriaBlocks,
    addCriteriaBlock,
    removeCriteriaBlock,
    setBlockType,
    setBlockCategory,
    setBlockOptions,
    setBlockEligibilityCriteria,
    setBlockOperand,
    setBlockValue,
    setBlockCeebCodes,
    setBlockRegimeIds,
    setBlockRangeKeys,
    setBlockRankIds,
    description: state.description,
    setDescription,
    isValid,
    toRequests,
  };
};
